import { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, LayoutDashboard, Bot, BarChart3, ShieldCheck,
  Calendar, Globe, Zap, Database, BrainCircuit,
  ChevronRight, ChevronLeft,
} from "lucide-react";
import authBg from "@/assets/auth-bg.jpg";
import slideDashboard from "@/assets/slide-dashboard.jpg";
import slideJournal from "@/assets/slide-journal.jpg";
import slideAnalytics from "@/assets/slide-analytics.jpg";
import slideMetatrader from "@/assets/slide-metatrader.jpg";

const SLIDES = [
  {
    image: slideDashboard,
    titleEn: "Professional Dashboard",
    titleFa: "داشبورد حرفه‌ای",
    descEn: "Real-time portfolio tracking with advanced financial metrics, equity curves, and account overview.",
    descFa: "ردیابی پرتفوی لحظه‌ای با معیارهای مالی پیشرفته، نمودار اکوئیتی و نمای کلی حساب.",
  },
  {
    image: slideJournal,
    titleEn: "Trade Journal",
    titleFa: "ژورنال معاملات",
    descEn: "Log every trade automatically from MetaTrader with detailed entry/exit analysis and tagging.",
    descFa: "ثبت خودکار هر معامله از متاتریدر با تحلیل دقیق ورود/خروج و برچسب‌گذاری.",
  },
  {
    image: slideAnalytics,
    titleEn: "Deep Analytics",
    titleFa: "تحلیل عمیق عملکرد",
    descEn: "Strategy performance, psychology patterns, mistake analysis, and win-rate breakdowns.",
    descFa: "عملکرد استراتژی، الگوهای روانشناسی، تحلیل اشتباهات و آمار نرخ برد.",
  },
  {
    image: slideMetatrader,
    titleEn: "MetaTrader Integration",
    titleFa: "اتصال به متاتریدر",
    descEn: "Seamless connection with MT4/MT5 via webhook for live position tracking and trade commands.",
    descFa: "اتصال بی‌واسطه با MT4/MT5 از طریق وب‌هوک برای ردیابی زنده پوزیشن‌ها.",
  },
];

const FEATURES = [
  { icon: LayoutDashboard, labelEn: "Dashboard", labelFa: "داشبورد" },
  { icon: Bot, labelEn: "Assistant", labelFa: "دستیار" },
  { icon: BarChart3, labelEn: "Analytics", labelFa: "تحلیل" },
  { icon: ShieldCheck, labelEn: "Risk", labelFa: "ریسک" },
  { icon: Calendar, labelEn: "Calendar", labelFa: "تقویم" },
  { icon: Globe, labelEn: "Fundamentals", labelFa: "فاندامنتال" },
];

const STATS = [
  { icon: Zap, valueEn: "10+", labelEn: "Professional Tools", labelFa: "ابزار حرفه‌ای" },
  { icon: Database, valueEn: "Real-time", labelEn: "Market Data", labelFa: "داده لحظه‌ای" },
  { icon: BrainCircuit, valueEn: "AI", labelEn: "Smart Analytics", labelFa: "تحلیل هوشمند" },
];

interface Props {
  isRTL: boolean;
}

export const AuthMarketingPanel = ({ isRTL }: Props) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goSlide = useCallback((dir: 1 | -1) => {
    setCurrentSlide((prev) => (prev + dir + SLIDES.length) % SLIDES.length);
  }, []);

  const slide = SLIDES[currentSlide];

  return (
    <div className="hidden lg:flex lg:w-[58%] relative flex-col overflow-hidden">
      {/* Background image */}
      <img
        src={authBg}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        aria-hidden="true"
        fetchPriority="high"
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/70" />

      <div className="relative z-10 flex flex-col h-full p-8 xl:p-12 justify-between text-white">
        {/* Brand */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10">
            <TrendingUp className="h-7 w-7 text-emerald-400" />
          </div>
          <span className="text-xl font-bold tracking-tight">
            {isRTL ? "ژورنال معاملات" : "Trade Journal"}
          </span>
        </div>

        {/* Tagline */}
        <div className="mt-6">
          <h1 className="text-3xl xl:text-4xl font-extrabold leading-tight">
            {isRTL
              ? "همراه حرفه‌ای معاملات شما"
              : "Your Professional Trading Companion"}
          </h1>
          <p className="mt-3 text-base text-white/60 max-w-lg">
            {isRTL
              ? "مدیریت، تحلیل و بهینه‌سازی معاملات در یک پلتفرم یکپارچه"
              : "Manage, analyze, and optimize your trades in one unified platform"}
          </p>
        </div>

        {/* Screenshot carousel */}
        <div className="flex-1 flex flex-col justify-center max-w-2xl mt-6">
          <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4 shadow-2xl">
            {/* Screenshot */}
            <div className="rounded-xl overflow-hidden border border-white/10 shadow-lg">
              <img
                src={slide.image}
                alt={isRTL ? slide.titleFa : slide.titleEn}
                className="w-full h-48 xl:h-56 object-cover transition-opacity duration-500"
                width={636}
                height={224}
                loading="lazy"
                decoding="async"
              />
            </div>

            {/* Title + description */}
            <div className="mt-4 px-1">
              <h2 className="text-lg font-bold">
                {isRTL ? slide.titleFa : slide.titleEn}
              </h2>
              <p className="text-sm text-white/50 mt-1 leading-relaxed">
                {isRTL ? slide.descFa : slide.descEn}
              </p>
            </div>

            {/* Nav */}
            <div className="flex items-center justify-between mt-4 px-1">
              <button
                onClick={() => goSlide(-1)}
                className="p-1.5 rounded-full hover:bg-white/10 transition"
                aria-label={isRTL ? "اسلاید بعدی" : "Previous slide"}
              >
                {isRTL ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
              </button>
              <div className="flex gap-1">
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    aria-label={`Slide ${i + 1}`}
                    className="relative flex items-center justify-center min-w-[24px] min-h-[24px]"
                  >
                    <span className={`block h-2 rounded-full transition-all ${
                      i === currentSlide
                        ? "w-7 bg-emerald-400"
                        : "w-2 bg-white/25 hover:bg-white/40"
                    }`} />
                  </button>
                ))}
              </div>
              <button
                onClick={() => goSlide(1)}
                className="p-1.5 rounded-full hover:bg-white/10 transition"
                aria-label={isRTL ? "اسلاید قبلی" : "Next slide"}
              >
                {isRTL ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-4 mt-4">
          {STATS.map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/10 flex-1"
            >
              <s.icon className="h-5 w-5 text-emerald-400 shrink-0" />
              <div>
                <div className="text-sm font-bold">{s.valueEn}</div>
                <div className="text-xs text-white/50">
                  {isRTL ? s.labelFa : s.labelEn}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Feature icons */}
        <div className="grid grid-cols-6 gap-3 mt-4">
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition cursor-default backdrop-blur-sm"
            >
              <f.icon className="h-5 w-5 text-emerald-400" />
              <span className="text-[11px] text-white/60 text-center">
                {isRTL ? f.labelFa : f.labelEn}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
